import { Formik, FormikErrors, FormikProps } from 'formik';
import { FormButton } from '../../../_core/form/button/form-button.component';
import { IRIFormProps } from '../../../_core/form/form.types';
import { IRequest, IUser, REQUEST_NOTIFY_OPTION, requestNotifyOptionMultiselectLabels } from '../../../../lib/types';
import { ISendCustomRequestMessageFormParams, SendRequestMessageFormFields } from './send-request-message-form-fields';
import { FormActionsRow } from '../../../_core/form/input/form-actions-row';
import { getName } from '../../../../utils/name.utils';
import { useMemo } from 'react';
import { IMultiSelectOption } from '../../../_core/input/multiselect-input.component';

interface ISendRequestMessageFormProps extends IRIFormProps<ISendCustomRequestMessageFormParams> {
  clientMode: boolean;
  defaultMessage?: string;
  defaultNotify?: (REQUEST_NOTIFY_OPTION.CLIENTS | REQUEST_NOTIFY_OPTION.STAFF)[];
  forceSendAll?: boolean;
  isResponse?: boolean;
  request: IRequest;
  user?: IUser | null;
}

export const SendRequestMessageForm = ({
  clientMode,
  defaultMessage = 'You have been sent a new request',
  defaultNotify,
  forceSendAll,
  isResponse,
  onBack,
  onSubmit,
  request,
  user
}: ISendRequestMessageFormProps) => {
  const replyToOptions = useMemo(() => {
    const opts: IMultiSelectOption[] =
      clientMode && request.assignedTo
        ? [
            {
              value: request.assignedTo.user.email,
              label: getName(request.assignedTo),
              secondaryLabel: request.assignedTo.user.email
            },
            ...[...(request.clientEditorIds ?? []), ...(request.clientViewerIds ?? [])].map(
              (c) => ({ value: c.user.email, label: getName(c), secondaryLabel: c.user.email } as IMultiSelectOption)
            )
          ]
        : [
            {
              value: request.createdBy.email,
              label: getName(request.createdBy),
              secondaryLabel: request.createdBy.email
            },
            ...[...request.staffEditorIds, ...request.staffViewerIds].map(
              (s) => ({ value: s.user.email, label: getName(s), secondaryLabel: s.user.email } as IMultiSelectOption)
            )
          ];

    return opts.reduce(
      (acc: IMultiSelectOption[], curr) => (acc.find((o) => o.value === curr.value) ? acc : [...acc, curr]),
      []
    );
  }, [clientMode, request]);

  const validateForm = (values: ISendCustomRequestMessageFormParams) => {
    const errors: FormikErrors<ISendCustomRequestMessageFormParams> = {};

    // Send custom message validations only
    if (!forceSendAll) {
      if (!values.message) errors.message = 'Required';
      if (!values.subject) errors.subject = 'Required';
      if (values.groupsToggle && !values.notify?.length)
        errors.notify = 'Must specify at least one group or use To option';
      if (!values.groupsToggle && !values.to?.length)
        errors.to = 'Must specify at least one recipient or use notify option';
    }

    return errors;
  };

  return (
    <Formik
      initialValues={
        {
          message: defaultMessage,
          notify: (defaultNotify ?? (clientMode ? [REQUEST_NOTIFY_OPTION.STAFF] : [REQUEST_NOTIFY_OPTION.CLIENTS])).map(
            (value) => ({ label: requestNotifyOptionMultiselectLabels[value], value })
          ),
          to: clientMode
            ? [
                {
                  label: getName(request.createdBy),
                  secondaryLabel: request.createdBy.email,
                  value: request.createdBy._id
                }
              ]
            : request.assignedTo
            ? [
                {
                  label: getName(request.assignedTo),
                  secondaryLabel: request.assignedTo.user.email,
                  value: request.assignedTo._id
                }
              ]
            : [],
          replyTo: [],
          subject: clientMode
            ? isResponse
              ? 'Request Completed'
              : 'Request Update'
            : forceSendAll
            ? 'You have been sent a new request'
            : "You have received a message on one of your Request's"
        } as ISendCustomRequestMessageFormParams
      }
      onSubmit={({ replyTo, ...rest }) => onSubmit({ ...rest, replyTo: replyTo.filter((v) => v) })}
      validate={validateForm}
      validateOnBlur={false}
    >
      {(props: FormikProps<ISendCustomRequestMessageFormParams>) => (
        <SendRequestMessageFormFields
          {...props}
          clientMode={clientMode}
          forceSendAll={forceSendAll}
          replyToOptions={replyToOptions}
          request={request}
          userId={user?._id ?? ''}
        >
          <FormActionsRow>
            {!!onBack && <FormButton type="button" onPress={onBack} text="Cancel" variant="secondary" />}
            <FormButton
              loading={props.isSubmitting}
              disabled={props.isSubmitting}
              text={forceSendAll ? 'Send Request' : 'Send Message'}
            />
          </FormActionsRow>
        </SendRequestMessageFormFields>
      )}
    </Formik>
  );
};
